import type { Audience } from '@/lib/audience';
import type { DropdownType, HeaderBundle, MegaMenuType } from '@/types/header';

const NON_ALPHANUMERIC_UNICODE = new RegExp('[^\\p{L}\\p{N}]+', 'gu');

type MaybeString = string | null | undefined;

/** Locale-safe key: keeps letters from any script (e.g. Hindi), strips punctuation/space. */
export function normalizeNavKey(value: MaybeString): string {
  return (value ?? '').toLowerCase().normalize('NFKC').replace(NON_ALPHANUMERIC_UNICODE, '');
}

export function isExternalNavHref(href: MaybeString): boolean {
  return /^(https?:|mailto:|tel:|javascript:)/i.test((href ?? '').trim());
}

export function isPlaceholderNavHref(href: MaybeString): boolean {
  const value = (href ?? '').trim();
  return !value || value === '#' || value === '#!';
}

/** Strip a leading locale segment so `/hi` and `/` both normalize to home. */
export function stripLocalePrefix(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '/';
  const withoutLocale = trimmed.replace(/^\/(en|hi)(?=\/|$)/i, '') || '/';
  return withoutLocale.replace(/\/+$/, '') || '/';
}

const HOME_MENU_KEYS = new Set(['home', 'होम'].map(normalizeNavKey));

export function isHomeMegaMenuItem(item: Pick<MegaMenuType, 'menuTitle' | 'menuLink'>): boolean {
  const rawLink = (item.menuLink ?? '').trim();
  if (rawLink && !isExternalNavHref(rawLink)) {
    if (stripLocalePrefix(rawLink) === '/') return true;
  }
  return HOME_MENU_KEYS.has(normalizeNavKey(item.menuTitle));
}

export function getHomeNavLabel(bundle: HeaderBundle, fallback = 'Home'): string {
  const home = (bundle.megaMenu ?? []).find(isHomeMegaMenuItem);
  return home?.menuTitle?.trim() || fallback;
}

const AUDIENCE_HEADING_KEYS: Record<Audience, Set<string>> = {
  'home-builder': new Set(['homebuilder', 'home builder', 'घर निर्माता'].map(normalizeNavKey)),
  corporate: new Set(['corporate', 'कॉर्पोरेट'].map(normalizeNavKey)),
  infrastructure: new Set(
    ['infrastructure', 'बुनियादी ढांचा', 'बुनियादीढांचा'].map(normalizeNavKey),
  ),
};

function collectDropdownUrls(dropdown: DropdownType): string[] {
  return [
    dropdown.dropdownLink,
    ...(dropdown.navlinks ?? []).map((n) => n.navUrl),
    ...(dropdown.subDropDown ?? []).flatMap((section) =>
      (section.subDropdown ?? []).map((item) => item.navUrl),
    ),
  ]
    .filter((url): url is string => Boolean(url?.trim()))
    .map((url) => url.toLowerCase());
}

export function matchesAudienceDropdown(dropdown: DropdownType, audience: Audience): boolean {
  const headingKey = normalizeNavKey(dropdown.dropdownHeading);
  if (AUDIENCE_HEADING_KEYS[audience].has(headingKey)) return true;

  const urls = collectDropdownUrls(dropdown);

  if (audience === 'home-builder') {
    return urls.some(
      (url) =>
        url.includes('home-building-guide') ||
        url.includes('construction-cost-calculator') ||
        url.includes('dealer-locator') ||
        url.includes('build-your-home'),
    );
  }

  if (audience === 'infrastructure') {
    return urls.some((url) => url.includes('/infrastructure'));
  }

  // Corporate is heading-only — infrastructure also uses /about-us links.
  return false;
}

export function pickAudienceDropdown(dropdown: DropdownType[], audience: Audience): DropdownType[] {
  const match = dropdown.find((item) => matchesAudienceDropdown(item, audience));
  return match ? [match] : [];
}
